import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../../services/authService';
import candidateService from '../../../services/candidateService';
import dashboardService from '../../../services/dashboardService';
import Header from '../../../components/ui/Header';
import FixedSidebar from '../../../components/ui/FixedSidebar';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { hasFeatureAccess, hasActionAccess } from '../../../utils/roleBasedAccess';

/**
 * HR Recruiter Dashboard
 * Focused on day-to-day recruitment activities and candidate management
 */
const HRRecruiterDashboard = () => {
  const [candidates, setCandidates] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [todayStats, setTodayStats] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const loadRecruiterData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get current user
        const user = authService.getStoredUser() || await authService.getCurrentUser();
        setCurrentUser(user);
        
        // Load recent candidates
        const candidateData = await candidateService.getCandidates({ 
          pageSize: 20,
          sortBy: 'created_at',
          sortOrder: 'desc'
        });
        
        // Ensure candidates is always an array
        const candidatesArray = Array.isArray(candidateData) ? candidateData : 
                               (candidateData?.candidates || candidateData?.data || []);
        
        console.log('Candidates data received:', candidateData);
        console.log('Processed candidates array:', candidatesArray);
        
        setCandidates(candidatesArray);
        
        // Load today's statistics
        const stats = await dashboardService.getDashboardOverview('admin');
        setTodayStats(stats || {});
        
      } catch (error) {
        console.error('[HRRecruiterDashboard] Error loading data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadRecruiterData();
  }, []);

  const notificationCounts = {
    candidates: candidates.filter(c => c.status === 'new').length,
    processing: candidates.filter(c => c.status === 'screening').length,
    interviews: candidates.filter(c => c.status === 'interview').length,
    total: candidates.filter(c => ['new', 'screening', 'interview'].includes(c.status)).length
  };

  const recruiterMetrics = [
    {
      title: 'My Pipeline',
      value: candidates.length.toString(),
      change: '+12',
      changeType: 'increase',
      icon: 'Users',
      color: 'primary',
      description: 'Active candidates'
    },
    {
      title: 'Interviews Today',
      value: '3',
      change: '+1',
      changeType: 'increase',
      icon: 'Calendar',
      color: 'success',
      description: 'Scheduled for today'
    },
    {
      title: 'Reviews Needed',
      value: candidates.filter(c => c.status === 'screening').length.toString(),
      change: '-2',
      changeType: 'decrease',
      icon: 'FileText',
      color: 'warning',
      description: 'Pending my review'
    },
    {
      title: 'Success Rate',
      value: '78%',
      change: '+5%',
      changeType: 'increase',
      icon: 'TrendingUp',
      color: 'success',
      description: 'Interview to hire ratio'
    }
  ];

  const todayTasks = [
    {
      id: 1,
      title: 'Review Sarah Johnson\'s profile',
      type: 'review',
      priority: 'high',
      dueTime: '10:00 AM',
      status: 'pending'
    },
    {
      id: 2,
      title: 'Interview with Alex Rodriguez',
      type: 'interview',
      priority: 'high',
      dueTime: '2:00 PM',
      status: 'confirmed'
    },
    {
      id: 3,
      title: 'Follow up with Michael Chen',
      type: 'follow-up',
      priority: 'medium',
      dueTime: '4:00 PM',
      status: 'pending'
    },
    {
      id: 4,
      title: 'Update candidate status reports',
      type: 'admin',
      priority: 'low',
      dueTime: '5:00 PM',
      status: 'in-progress'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header currentUser={currentUser} notificationCounts={notificationCounts} />
        <FixedSidebar currentPath="/hr-roles/recruiter" />
        <main className="ml-72 pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading recruiter dashboard...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header currentUser={currentUser} notificationCounts={notificationCounts} />
        <FixedSidebar currentPath="/hr-roles/recruiter" />
        <main className="ml-72 pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Failed to Load Dashboard</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header currentUser={currentUser} notificationCounts={notificationCounts} />
      <FixedSidebar currentPath="/hr-roles/recruiter" />
      
      <main className="ml-72 pt-16">
        <div className="p-6">
          <Breadcrumb customBreadcrumbs={[
            { label: 'HR Roles', href: '/hr-roles' },
            { label: 'Recruiter Dashboard', href: '/hr-roles/recruiter', isActive: true }
          ]} />

          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Recruiter Dashboard</h1>
              <p className="text-muted-foreground">
                Your daily recruitment activities and candidate pipeline
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button variant="outline" onClick={() => navigate('/cv-upload-processing')}>
                <Icon name="Upload" size={16} className="mr-2" />
                Upload CVs
              </Button>
              <Button onClick={() => navigate('/interview-scheduling')}>
                <Icon name="Calendar" size={16} className="mr-2" />
                Schedule Interview
              </Button>
            </div>
          </div>

          {/* Recruiter Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
            {recruiterMetrics.map((metric, index) => (
              <div key={index} className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    metric.color === 'success' ? 'bg-green-100' :
                    metric.color === 'warning' ? 'bg-yellow-100' :
                    metric.color === 'primary' ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    <Icon name={metric.icon} size={24} className={
                      metric.color === 'success' ? 'text-green-600' :
                      metric.color === 'warning' ? 'text-yellow-600' :
                      metric.color === 'primary' ? 'text-blue-600' : 'text-gray-600'
                    } />
                  </div>
                  <span className={`text-sm font-medium ${
                    metric.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {metric.change}
                  </span>
                </div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">{metric.title}</h3>
                <p className="text-2xl font-bold text-foreground mb-1">{metric.value}</p>
                <p className="text-xs text-muted-foreground">{metric.description}</p>
              </div>
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
            {/* Today's Tasks */}
            <div className="xl:col-span-1 bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-6">Today's Tasks</h3>
              <div className="space-y-4">
                {todayTasks.map((task) => (
                  <div key={task.id} className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      task.priority === 'high' ? 'bg-red-100' :
                      task.priority === 'medium' ? 'bg-yellow-100' : 'bg-green-100'
                    }`}>
                      <Icon 
                        name={
                          task.type === 'interview' ? 'Calendar' :
                          task.type === 'review' ? 'FileText' :
                          task.type === 'follow-up' ? 'Phone' : 'Settings'
                        } 
                        size={16} 
                        className={
                          task.priority === 'high' ? 'text-red-600' :
                          task.priority === 'medium' ? 'text-yellow-600' : 'text-green-600'
                        } 
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{task.title}</p>
                      <p className="text-xs text-muted-foreground">{task.dueTime}</p>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs ${
                      task.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                      task.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {task.status}
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" className="w-full mt-4">
                View All Tasks
              </Button>
            </div>

            {/* Recent Candidates */}
            <div className="xl:col-span-2 bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-foreground">Recent Candidates</h3>
                <Button variant="outline" size="sm" onClick={() => navigate('/candidate-database')}>
                  View All
                </Button>
              </div>
              <div className="space-y-4">
                {candidates.slice(0, 5).map((candidate, index) => (
                  <div key={candidate.id || index} className="flex items-center space-x-4 p-4 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors cursor-pointer"
                       onClick={() => navigate(`/candidate-profile-detail?id=${candidate.id}`)}>
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="User" size={20} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{candidate.name || 'Unknown Candidate'}</p>
                      <p className="text-sm text-muted-foreground">{candidate.position || 'No position specified'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{candidate.ai_score || candidate.aiScore || 'N/A'}</p>
                      <div className={`px-2 py-1 rounded-full text-xs ${
                        candidate.status === 'new' ? 'bg-blue-100 text-blue-800' :
                        candidate.status === 'screening' ? 'bg-yellow-100 text-yellow-800' :
                        candidate.status === 'interview' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {candidate.status || 'unknown'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Actions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Icon name="Search" size={20} className="text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold">Find Candidates</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Search and filter through your candidate database
              </p>
              <Button variant="outline" className="w-full" onClick={() => navigate('/candidate-database')}>
                Browse Database
              </Button>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Icon name="Calendar" size={20} className="text-green-600" />
                </div>
                <h3 className="text-lg font-semibold">Schedule Interviews</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Coordinate and manage interview schedules
              </p>
              <Button variant="outline" className="w-full" onClick={() => navigate('/interview-scheduling')}>
                View Calendar
              </Button>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Icon name="BarChart3" size={20} className="text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold">My Performance</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Track your recruitment metrics and goals
              </p>
              <Button variant="outline" className="w-full" onClick={() => navigate('/performance')}>
                View Analytics
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HRRecruiterDashboard;